import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

const StatCard = ({ label, value, sub, accent }) => (
  <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px' }}>
    <p style={{ fontSize: '12px', color: '#999', marginBottom: '6px' }}>{label}</p>
    <p style={{ fontSize: '28px', fontWeight: '600', color: accent || '#111', marginBottom: '2px' }}>{value}</p>
    {sub && <p style={{ fontSize: '12px', color: '#aaa' }}>{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const loadStats = () => {
    setLoading(true);
    api.get('/tickets/stats').then(({ data }) => { setStats(data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => {
    loadStats();
    const onFocus = () => loadStats();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (loading) return <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>Loading dashboard...</div>;
  if (!stats) return (
    <div style={{ padding: '40px', textAlign: 'center' }}>
      <p style={{ color: '#c00', marginBottom: '12px' }}>Could not load stats</p>
      <button onClick={loadStats} style={{ padding: '8px 20px', background: '#185FA5', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', cursor: 'pointer' }}>Retry</button>
    </div>
  );

  const byStatus = Object.fromEntries((stats.byStatus || []).map(r => [r.status, parseInt(r.count)]));
  const open = byStatus.open || 0;
  const pending = byStatus.pending || 0;
  const resolved = byStatus.resolved || 0;

  return (
    <div>
      <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '20px' }}>Dashboard</h2>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '24px' }}>
        <StatCard label="Open tickets" value={open} sub={`${stats.unassigned} unassigned`} accent={stats.unassigned > 0 ? '#c00' : undefined} />
        <StatCard label="Pending" value={pending} />
        <StatCard label="Resolved" value={resolved} />
        <StatCard label="Avg first response" value={stats.avgResponseHours ? `${stats.avgResponseHours}h` : '—'} sub="last 7 days" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>

        {/* Agent workload */}
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>Agent workload</h3>
          {(stats.byAgent || []).length === 0 && <p style={{ color: '#bbb', fontSize: '13px' }}>No data yet</p>}
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
                <th style={{ textAlign: 'left', padding: '6px 0', color: '#999', fontWeight: '500' }}>Agent</th>
                <th style={{ textAlign: 'center', padding: '6px 0', color: '#999', fontWeight: '500' }}>Open</th>
                <th style={{ textAlign: 'center', padding: '6px 0', color: '#999', fontWeight: '500' }}>Resolved today</th>
              </tr>
            </thead>
            <tbody>
              {(stats.byAgent || []).map(agent => (
                <tr key={agent.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                  <td style={{ padding: '8px 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: '#CECBF6', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px', fontWeight: '600', color: '#3C3489', flexShrink: 0 }}>
                        {agent.name.slice(0, 2).toUpperCase()}
                      </div>
                      {agent.name}
                    </div>
                  </td>
                  <td style={{ textAlign: 'center', padding: '8px 0', fontWeight: '500' }}>{agent.open_count}</td>
                  <td style={{ textAlign: 'center', padding: '8px 0', color: '#27500A' }}>{agent.resolved_today}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick actions */}
        <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: '10px', padding: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '14px' }}>Quick access</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { label: 'View all open tickets', params: '?status=open', color: '#0C447C' },
              { label: 'View urgent tickets', params: '?priority=urgent', color: '#791F1F' },
              { label: 'View unassigned tickets', params: '?assigned=unassigned', color: '#633806' },
              { label: 'View my tickets', params: '?assigned=me', color: '#27500A' },
            ].map(item => (
              <button key={item.label} onClick={() => navigate(`/tickets${item.params}`)}
                style={{ padding: '10px 14px', border: '1px solid #e8e8e8', borderRadius: '8px', fontSize: '13px', cursor: 'pointer', background: '#fafafa', textAlign: 'left', color: item.color, fontWeight: '500' }}>
                {item.label} →
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
